/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 *
 * The alert behaviour a configuration is built from.
 *
 * Every alert configuration names one behaviour template in `alertBehaviorId`, and that single
 * field decides what the alert watches for and which settings the rest of the document even has.
 * It is the first thing the configuration screen shows, so it is the first thing the panel shows.
 *
 * The id carries the name: `alert-behavior:Left Geofence with Door Open`. Older configurations
 * carry a version suffix too — `alert-behavior:Door Alert/ver1.0.0` — from behaviour templates that
 * have since been retired. Around 270 live configurations still reference those 18 retired
 * templates, so the version is parsed out and shown rather than left to disfigure the name.
 *
 * Descriptions are transcribed from `alert-behaviors-v2` for the 24 current templates, for the same
 * reason `alertSettings.ts` transcribes its table: CouchDB is not reachable from api-insights. A
 * behaviour without one — every retired template — simply shows its name.
 *
 * Regenerate after a behaviour changes:
 *   curl -su "$COUCHDB_USER:$COUCHDB_PASS" \
 *     "$COUCHDB_URL/alert-behaviors-v2/_all_docs?include_docs=true"
 * then read `name` and `shortDescription`.
 */

const ID_PREFIX = 'alert-behavior:'
const VERSION_MARKER = '/ver'

export interface AlertBehaviour {
  /** The behaviour's name, e.g. `Left Geofence with Door Open`. */
  name: string
  /** The version suffix of a retired template, e.g. `1.0.0`. Absent for current behaviours. */
  version?: string
  /** What the behaviour does, for the 24 templates currently published. */
  description?: string
}

/** Behaviour name → the one-line description the configuration screen shows beside it. */
export const BEHAVIOUR_DESCRIPTIONS: Record<string, string | undefined> = {
  Door: "Alert mainly focused on Door/Contact Monitoring Point's state (open or close) potentially along with other criteria",
  'Fridge Alarms (Includes all RED, YELLOW, GREEN)': 'Alerts when alarm codes are received from reefer',
  'Fridge Alarms GREEN only': 'Alerts when green alarm codes are received from reefer',
  'Fridge Alarms RED and YELLOW only': 'Alerts when red and yellow alarm codes are received from reefer',
  'Fridge Alarms RED only': 'Alerts when red alarm codes are received from reefer',
  'Fridge Alarms YELLOW only': 'Alerts when yellow alarm codes are received from reefer',
  'Geofence Entry': 'Alerts when an asset enters the specified geofence category or geofences',
  'Geofence Exit': 'Alerts when an asset exits the specified geofence category',
  'Left Geofence with Door Open':
    'Alert primarily focused on the Door/Contact Monitoring Point being in an open state while the asset is crossing outside specified geofences, potentially along with additional criteria',
  'Low Fuel': 'Alerts when fuel level is low',
  'Low Reefer Battery Voltage Alert': 'Alerts when reefer battery voltage is low',
  'Monitoring Point Offline': 'Raises an alert when a monitoring point has been offline for a set amount of time',
  'Monitoring Point Temperature': "Alerts when a monitoring point's temperature is outside a user-defined range",
  'Monitoring Point Temperature - Outside DC - Fridge On':
    'Temperature out of range when outside of DC and the fridge is on',
  'Operational Mode Violation':
    'Alert is raised (after delay if delays are enabled) when reefer operational mode is any of the specified operations',
  'Reefer Off after Leaving Geofences': 'Alerts when the fridge is off outside of geofences',
  'Reefer Off while in Motion': 'Alerts when the fridge is off while the asset is moving',
  'Setpoint-based Monitoring Point Temperature':
    "When a monitoring point's temperature goes out of range based on the specified zone's setpoint",
  Speed: "Alerts when an Asset's max or average speed exceed a configured threshold",
  'Tag Turned Off': 'Alerts when a Cooltrax Tag is turned off. Has no effect on non-Cooltrax Tags',
  'UMD External Power Lost': 'Raises an alert when a UMD loses power and changes to internal power source',
  'UMD Offline': 'This Alert triggers when a UMD has been offline for a specified amount of time',
  'Virtual Setpoint-based Monitoring Point Temperature':
    "Alerts with the configured severity when the temperature goes out of range based on the specified zone's virtual setpoint",
  'Zone Setpoint Outside Range': 'When the zone setpoint temperature goes out of range',
}

/**
 * Read a behaviour id. Returns `undefined` only for a value that carries no name at all, so an
 * unrecognised behaviour still renders as itself.
 */
export const parseBehaviour = (raw: unknown): AlertBehaviour | undefined => {
  if (typeof raw !== 'string' || raw === '') return undefined

  const withoutPrefix = raw.startsWith(ID_PREFIX) ? raw.slice(ID_PREFIX.length) : raw
  if (withoutPrefix === '') return undefined

  const marker = withoutPrefix.lastIndexOf(VERSION_MARKER)
  const name = marker === -1 ? withoutPrefix : withoutPrefix.slice(0, marker)
  const version = marker === -1 ? undefined : withoutPrefix.slice(marker + VERSION_MARKER.length)

  return {
    name,
    version: version || undefined,
    description: BEHAVIOUR_DESCRIPTIONS[name],
  }
}

/** The behaviour's name with its version, when it has one: `Door Alert (v1.0.0)`. */
export const behaviourLabel = (raw: unknown): string | undefined => {
  const behaviour = parseBehaviour(raw)
  if (!behaviour) return undefined
  return behaviour.version ? `${behaviour.name} (v${behaviour.version})` : behaviour.name
}
